import { FC, type ReactNode } from 'react';

import SearchIcon from '@/assets/svg/ui-elements/search.svg';
import { Avatar, Badge, Input, Modal, theme, Tooltip, Typography } from '@/shared/design-system';
import type { PendingMemberChange } from '@/utils/role-request-activity';

import { MemberIdentity, MemberIdentityRow, MemberList, MemberRow, MemberRowMain } from '../styled';

/** Under this many holders the list fits on screen and a search box is noise. */
export const MEMBER_SEARCH_THRESHOLD = 9;
export const WAITING_TO_JOIN_LABEL = 'Waiting for approval';
export const REMOVAL_PENDING_LABEL = 'Removal pending';
export const SELF_BADGE = 'You';

export const matchesMember = (needle: string, ...fields: (string | null | undefined)[]) =>
  !needle || fields.some((field) => field?.toLowerCase().includes(needle));

export const MemberSearch: FC<{ value: string; onChange: (_value: string) => void }> = ({
  value,
  onChange,
}) => (
  <Input
    size="sm"
    value={value}
    placeholder="Search members"
    aria-label="Search members"
    startIcon={<SearchIcon />}
    onChange={(event) => onChange(event.target.value)}
  />
);

export type MemberIdentityViewProps = {
  primary: string;
  secondary?: string | null;
  isSelf?: boolean;
  pendingRemoval?: PendingMemberChange;
  badges?: ReactNode;
};

/** Avatar, name and email, with the states a holder of this role can be in. */
export const MemberIdentityView: FC<MemberIdentityViewProps> = ({
  primary,
  secondary,
  isSelf,
  pendingRemoval,
  badges,
}) => (
  <MemberRowMain>
    <Avatar size="sm" type="initials" initials={primary} />
    <MemberIdentity>
      <MemberIdentityRow>
        <Typography variant="body3" color={theme.colors.text.primary} noWrap>
          {primary}
        </Typography>
        {isSelf && <Badge size="sm" variant="subtle" color="primary" label={SELF_BADGE} />}
        {pendingRemoval && (
          <Tooltip title={`${pendingRemoval.initiatorLabel} asked to remove this member.`}>
            <span>
              <Badge size="sm" variant="subtle" color="yellow" label={REMOVAL_PENDING_LABEL} />
            </span>
          </Tooltip>
        )}
        {badges}
      </MemberIdentityRow>
      {secondary && (
        <Typography variant="body4" color={theme.colors.text.tertiary} noWrap>
          {secondary}
        </Typography>
      )}
    </MemberIdentity>
  </MemberRowMain>
);

/** People an assign request names who are not holders yet: they appear once it is approved. */
export const PendingMemberRows: FC<{ changes: readonly PendingMemberChange[] }> = ({ changes }) => {
  const joining = changes.filter((change) => change.assign);
  if (!joining.length) return null;

  return (
    <MemberList aria-label="Waiting for approval">
      {joining.map((change) => (
        <MemberRow key={change.requestId}>
          <MemberIdentityView
            primary={change.label}
            secondary={`Requested by ${change.initiatorLabel}`}
            badges={
              <Badge size="sm" variant="subtle" color="yellow" label={WAITING_TO_JOIN_LABEL} />
            }
          />
        </MemberRow>
      ))}
    </MemberList>
  );
};

export type RemoveMemberDialogProps = {
  open: boolean;
  memberLabel: string;
  roleName: string;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const RemoveMemberDialog: FC<RemoveMemberDialogProps> = ({
  open,
  memberLabel,
  roleName,
  isPending,
  onClose,
  onConfirm,
}) => (
  <Modal
    open={open}
    onOpenChange={(isOpen) => !isOpen && onClose()}
    variant="center"
    title={`Remove ${memberLabel} from ${roleName}?`}
    description="They keep whatever their other roles grant. If you can't approve role changes, this goes to an approver first."
    primaryAction={{
      label: isPending ? 'Submitting…' : 'Remove from role',
      color: 'destructive',
      disabled: isPending,
      onClick: onConfirm,
    }}
    secondaryAction={{ label: 'Cancel', onClick: onClose }}
  >
    <></>
  </Modal>
);
